import { supabase } from '../supabase';
import { useAuth } from '../auth';
import { adminUsers } from '../adminApi';
import {
  Instrument, Level, Course, AcademicYear, Student, GroupSubject,
  Teacher, CourseTemplate, CourseTemplateSubject, TeacherCourseSubject,
  ReportCardState, CriteriaGroup, Section, Field, EvaluationCriterion,
  User, Permission, Center, EvaluationPeriod, EvaluationPeriodConfig, EmailHistoryRecord
} from '../../types';
import { OAuthCredentials, EmailConfig, EmailTemplate } from '../../types/email';
import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'tu_clave_secreta_muy_larga_y_compleja';
const encryptPassword = (password: string): string => CryptoJS.AES.encrypt(password, ENCRYPTION_KEY).toString();
const decryptPassword = (encryptedPassword: string): string => CryptoJS.AES.decrypt(encryptedPassword, ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8);

function throwIfError(error: any) {
  if (error) throw new Error(error.message);
}

// ── Helper: obtener el center_id del usuario actual ──
export function getCenterId(): string {
  const user = useAuth.getState().user;
  if (!user?.centerId) {
    // Session from before migration — force logout
    console.warn('Sesión sin centerId, forzando cierre de sesión...');
    useAuth.getState().logout();
    // Clear localStorage directly in case persist middleware is stale
    try { localStorage.removeItem('auth-storage'); } catch {}
    throw new Error('Sesión obsoleta. Se ha cerrado la sesión automáticamente. Vuelve a entrar.');
  }
  return user.centerId;
}

export function isGlobalAdmin(): boolean {
  const user = useAuth.getState().user;
  return user?.role === 'admin';
}

// ── Tipos locales ──
interface GradeData {
  attendance: string;
  global: string;
  criteria: { [fieldId: string]: string };
  observations?: string;
}
interface Grades { [sectionId: string]: GradeData; }
interface AdditionalCriterion { sectionId: string; fields: Field[]; }

// ══════════════════════════════════════════════════════════
// INSTRUMENTOS
// ══════════════════════════════════════════════════════════

export async function getInstruments(): Promise<Instrument[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('instruments').select('id, name').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name }));
}

export async function createInstrument(name: string): Promise<Instrument> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('instruments').insert({ name, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name };
}

export async function updateInstrument(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('instruments').update({ name }).eq('id', id);
  throwIfError(error);
}

export async function deleteInstrument(id: string): Promise<void> {
  const { error } = await supabase.from('instruments').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// NIVELES
// ══════════════════════════════════════════════════════════

export async function getLevels(): Promise<Level[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('levels').select('id, name').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name }));
}

export async function createLevel(name: string): Promise<Level> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('levels').insert({ name, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name };
}

export async function updateLevel(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('levels').update({ name }).eq('id', id);
  throwIfError(error);
}

export async function deleteLevel(id: string): Promise<void> {
  const { error } = await supabase.from('levels').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// CURSOS
// ══════════════════════════════════════════════════════════

export async function getCourses(): Promise<Course[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase
    .from('courses')
    .select('id, name, level_id, template_id, academic_year_id, levels(name), academic_years(name), course_templates!fk_courses_template(id, academic_year)')
    .eq('center_id', centerId)
    .order('name');
  throwIfError(error);
  return (data || []).map((row: any) => ({
    id: row.id,
    name: row.name,
    levelId: row.level_id,
    levelName: row.levels?.name || '',
    academicYear: row.academic_years?.name || '',
    templateId: row.template_id || undefined
  }));
}

/**
 * Traduce lo que llegue como año de un curso a lo que guarda la columna
 * academic_year_id. Las pantallas no son coherentes entre sí: unas mandan el
 * id, otras el nombre ("2025-26") y otras nada. Vacío significa "curso
 * permanente", válido para todos los años, que es como funcionan los cursos
 * en esta app: 1º a 6º se reutilizan cada año y el año se guarda en las notas.
 */
async function resolverAnoCurso(valor: string | null | undefined): Promise<string | null> {
  if (!valor) return null;
  if (/^[0-9a-f-]{36}$/i.test(valor)) return valor;
  const { data } = await supabase.from('academic_years')
    .select('id').eq('name', valor).eq('center_id', getCenterId()).maybeSingle();
  return data?.id ?? null;
}

export async function createCourse(
  name: string, levelId: string, academicYear: string, templateId: string | null
): Promise<Course> {
  const centerId = getCenterId();
  const { data: level } = await supabase.from('levels').select('name').eq('id', levelId).single();
  const { data, error } = await supabase.from('courses')
    .insert({ name, level_id: levelId, academic_year_id: await resolverAnoCurso(academicYear), template_id: templateId, center_id: centerId })
    .select().single();
  throwIfError(error);
  return { id: data.id, name, levelId, levelName: level?.name || '', academicYear, templateId: templateId || undefined };
}

export async function updateCourse(
  id: string, name: string, levelId: string, academicYear: string, templateId: string | null
): Promise<Course> {
  const { data: level } = await supabase.from('levels').select('name').eq('id', levelId).single();
  const { error } = await supabase.from('courses')
    .update({ name, level_id: levelId, academic_year_id: await resolverAnoCurso(academicYear), template_id: templateId })
    .eq('id', id);
  throwIfError(error);
  return { id, name, levelId, levelName: level?.name || '', academicYear, templateId: templateId || undefined };
}

export async function deleteCourse(id: string): Promise<void> {
  const { error } = await supabase.from('courses').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// AÑOS ACADÉMICOS
// ══════════════════════════════════════════════════════════

export async function getAcademicYears(): Promise<AcademicYear[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('academic_years').select('*').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name, isActive: r.is_active }));
}

export async function createAcademicYear(name: string): Promise<AcademicYear> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('academic_years').insert({ name, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name, isActive: data.is_active };
}

export async function updateAcademicYear(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('academic_years').update({ name }).eq('id', id);
  throwIfError(error);
}

export async function toggleAcademicYear(id: string, isActive: boolean): Promise<void> {
  if (isActive) {
    const centerId = getCenterId();
    // Desactivar todos del mismo centro primero
    await supabase.from('academic_years').update({ is_active: false }).eq('center_id', centerId);
  }
  const { error } = await supabase.from('academic_years').update({ is_active: isActive }).eq('id', id);
  throwIfError(error);
}

export async function deleteAcademicYear(id: string): Promise<void> {
  const { error } = await supabase.from('academic_years').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// ALUMNOS
// ══════════════════════════════════════════════════════════

function mapStudent(row: any): Student {
  return {
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    identifier: row.identifier,
    email: row.email,
    phone: row.phone,
    address: row.address,
    birthDate: row.birth_date,
    courseId: row.course_id,
    instrumentId: row.instrument_id,
    instrumentName: row.instruments?.name || '',
    courseName: row.courses?.name || '',
    levelName: row.courses?.levels?.name || '',
    puebloId: row.pueblo_id || null,
    puebloName: row.pueblos?.name || null,
  };
}

const STUDENT_SELECT = '*, instruments(name), courses(name, levels(name)), pueblos(name)';

export async function getStudentsByCourse(courseId: string): Promise<Student[]> {
  const { data, error } = await supabase.from('students')
    .select(STUDENT_SELECT)
    .eq('course_id', courseId)
    .order('first_name').order('last_name');
  throwIfError(error);
  return (data || []).map(mapStudent);
}

export async function getStudents(): Promise<Student[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('students')
    .select(STUDENT_SELECT)
    .eq('center_id', centerId)
    .order('first_name').order('last_name');
  throwIfError(error);
  return (data || []).map(mapStudent);
}

export async function createStudent(student: Omit<Student, 'id'>): Promise<Student> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('students')
    .insert({
      first_name: student.firstName, last_name: student.lastName,
      identifier: student.identifier, email: student.email,
      phone: student.phone || null, address: student.address || null,
      birth_date: student.birthDate || null,
      course_id: student.courseId, instrument_id: student.instrumentId,
      pueblo_id: student.puebloId || null,
      center_id: centerId
    })
    .select(STUDENT_SELECT).single();
  throwIfError(error);
  return mapStudent(data);
}

export async function updateStudent(id: string, student: Omit<Student, 'id'>): Promise<Student> {
  const { error } = await supabase.from('students')
    .update({
      first_name: student.firstName, last_name: student.lastName,
      identifier: student.identifier, email: student.email,
      phone: student.phone || null, address: student.address || null,
      birth_date: student.birthDate || null,
      course_id: student.courseId, instrument_id: student.instrumentId,
      pueblo_id: student.puebloId || null
    })
    .eq('id', id);
  throwIfError(error);

  const { data, error: err2 } = await supabase.from('students')
    .select(STUDENT_SELECT).eq('id', id).single();
  throwIfError(err2);
  return mapStudent(data);
}

export async function deleteStudents(ids: string[]): Promise<void> {
  const { error } = await supabase.from('students').delete().in('id', ids);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// ASIGNATURAS GRUPALES
// ══════════════════════════════════════════════════════════

export async function getGroupSubjects(): Promise<GroupSubject[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('group_subjects').select('id, name').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name }));
}

export async function createGroupSubject(name: string): Promise<GroupSubject> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('group_subjects').insert({ name, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name };
}

export async function updateGroupSubject(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('group_subjects').update({ name }).eq('id', id);
  throwIfError(error);
}

export async function deleteGroupSubject(id: string): Promise<void> {
  const { error } = await supabase.from('group_subjects').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// PROFESORES
// ══════════════════════════════════════════════════════════

export async function getTeachers(): Promise<Teacher[]> {
  const centerId = getCenterId();
  const [{ data: teachers, error: e1 }, { data: allTC }, { data: allTCS }] = await Promise.all([
    supabase.from('teachers').select('*, instruments(name)').eq('center_id', centerId),
    supabase.from('teacher_courses').select('teacher_id, course_id, courses(name)'),
    supabase.from('teacher_course_subjects').select('teacher_id, course_id, subject_name, subject_type, pueblo_id')
  ]);
  throwIfError(e1);

  return (teachers || []).map((t: any) => {
    const tc = (allTC || []).filter((c: any) => c.teacher_id === t.id);
    const tcs = (allTCS || []).filter((s: any) => s.teacher_id === t.id);
    const courseSubjects: any = {};
    tcs.forEach((s: any) => {
      if (!courseSubjects[s.course_id]) courseSubjects[s.course_id] = [];
      courseSubjects[s.course_id].push({ name: s.subject_name, type: s.subject_type, puebloId: s.pueblo_id ?? null });
    });
    return {
      id: t.id, firstName: t.first_name, lastName: t.last_name,
      identifier: t.identifier, email: t.email, username: t.username,
      instrumentId: t.instrument_id, instrumentName: t.instruments?.name || '',
      courseIds: tc.map((c: any) => c.course_id),
      courseNames: tc.map((c: any) => c.courses?.name || ''),
      courseSubjects
    };
  });
}

export async function createTeacher(teacherData: Omit<Teacher, 'id'>, courseIds: string[]): Promise<Teacher> {
  const centerId = getCenterId();
  // La contraseña solo se usa para dar de alta el usuario de acceso; la tabla
  // teachers ya no guarda copia (ver migrations/006).
  const password = teacherData.password || 'password';
  const { data, error } = await supabase.from('teachers')
    .insert({
      first_name: teacherData.firstName, last_name: teacherData.lastName,
      identifier: teacherData.identifier, email: teacherData.email,
      username: teacherData.username,
      instrument_id: teacherData.instrumentId,
      center_id: centerId
    })
    .select().single();
  throwIfError(error);
  const id = data.id;

  const defaultPermissions: Permission[] = ['view_students', 'view_courses', 'view_grades', 'edit_grades', 'view_report_cards'] as Permission[];
  await createUser({
    username: teacherData.username, password, email: teacherData.email,
    role: 'teacher', isActive: true, teacherId: id, permissions: defaultPermissions
  });

  if (courseIds.length > 0) {
    await supabase.from('teacher_courses').insert(courseIds.map(cid => ({ teacher_id: id, course_id: cid })));
  }

  return getTeacher(id);
}

export async function getTeacher(id: string): Promise<Teacher> {
  const { data, error } = await supabase.from('teachers')
    .select('*, instruments(name)').eq('id', id).single();
  throwIfError(error);

  const { data: tc } = await supabase.from('teacher_courses').select('course_id').eq('teacher_id', id);

  return {
    id: data.id, firstName: data.first_name, lastName: data.last_name,
    identifier: data.identifier, email: data.email, username: data.username,
    instrumentId: data.instrument_id,
    instrumentName: data.instruments?.name || '',
    courseIds: (tc || []).map((c: any) => c.course_id)
  };
}

export async function updateTeacher(id: string, teacherData: Partial<Teacher>, courseIds?: string[]): Promise<void> {
  const { error } = await supabase.from('teachers')
    .update({
      first_name: teacherData.firstName || '',
      last_name: teacherData.lastName || '',
      identifier: teacherData.identifier || '',
      email: teacherData.email || '',
      username: teacherData.username || '',
      instrument_id: teacherData.instrumentId || null
    })
    .eq('id', id);
  throwIfError(error);

  // Actualizar usuario asociado
  const { data: userRow } = await supabase.from('users').select('id').eq('teacher_id', id).maybeSingle();
  if (userRow) {
    const updateData: Partial<User> = {
      username: teacherData.username || '', email: teacherData.email || '',
      role: 'teacher', isActive: true, teacherId: id
    };
    if (teacherData.password) updateData.password = teacherData.password;
    await updateUser(userRow.id, updateData);
  }

  // Actualizar cursos
  await supabase.from('teacher_courses').delete().eq('teacher_id', id);
  if (courseIds && courseIds.length > 0) {
    await supabase.from('teacher_courses').insert(
      courseIds.map(cid => ({ teacher_id: id, course_id: cid }))
    );
  }
}

export async function deleteTeacher(id: string): Promise<void> {
  await supabase.from('users').delete().eq('teacher_id', id);
  await supabase.from('teacher_courses').delete().eq('teacher_id', id);
  await supabase.from('teacher_course_subjects').delete().eq('teacher_id', id);
  const { error } = await supabase.from('teachers').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// PLANTILLAS DE CURSO
// ══════════════════════════════════════════════════════════

export async function getCourseTemplates(): Promise<CourseTemplate[]> {
  const centerId = getCenterId();
  const [{ data: templates, error }, { data: allSubjects }] = await Promise.all([
    supabase.from('course_templates').select('*, levels(name)').eq('center_id', centerId),
    supabase.from('course_template_subjects').select('*').order('name')
  ]);
  throwIfError(error);

  return (templates || []).map((row: any) => {
    const subjects: CourseTemplateSubject[] = (allSubjects || [])
      .filter((s: any) => s.template_id === row.id)
      .map((s: any) => ({ id: s.id, name: s.name, type: s.type, isRequired: s.is_required }));
    return {
      id: row.id, name: row.name, levelId: row.level_id,
      courseId: row.course_id, academicYear: row.academic_year,
      levelName: row.levels?.name || '', subjects
    };
  });
}

export async function createCourseTemplate(template: Omit<CourseTemplate, 'id'>): Promise<CourseTemplate> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('course_templates')
    .insert({ name: template.name, level_id: template.levelId, course_id: template.courseId, academic_year: template.academicYear.toString(), center_id: centerId })
    .select().single();
  throwIfError(error);

  if (template.subjects.length > 0) {
    await supabase.from('course_template_subjects').insert(
      template.subjects.map((s: CourseTemplateSubject) => ({
        template_id: data.id, name: s.name, type: s.type, is_required: s.isRequired
      }))
    );
  }
  return { ...template, id: data.id };
}

export async function updateCourseTemplate(id: string, template: Omit<CourseTemplate, 'id' | 'levelName'>): Promise<void> {
  await supabase.from('course_templates').update({ name: template.name, level_id: template.levelId }).eq('id', id);
  await supabase.from('course_template_subjects').delete().eq('template_id', id);
  if (template.subjects.length > 0) {
    await supabase.from('course_template_subjects').insert(
      template.subjects.map((s: CourseTemplateSubject) => ({
        template_id: id, name: s.name, type: s.type, is_required: s.isRequired
      }))
    );
  }
}

export async function deleteCourseTemplate(id: string): Promise<void> {
  const { error } = await supabase.from('course_templates').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// ASIGNATURAS DE PROFESORES (CRA)
// ══════════════════════════════════════════════════════════

export async function assignTeacherSubjects(
  teacherId: string, courseId: string,
  subjects: Array<{ name: string; type: 'individual' | 'group'; puebloId?: string | null }>
): Promise<void> {
  await supabase.from('teacher_course_subjects').delete().eq('teacher_id', teacherId).eq('course_id', courseId);

  if (subjects.length === 0) {
    await supabase.from('teacher_courses').delete().eq('teacher_id', teacherId).eq('course_id', courseId);
    return;
  }

  // Asegurar que el curso está asignado (upsert ignore duplicates)
  await supabase.from('teacher_courses').upsert(
    { teacher_id: teacherId, course_id: courseId },
    { onConflict: 'teacher_id,course_id', ignoreDuplicates: true }
  );

  await supabase.from('teacher_course_subjects').insert(
    subjects.map(s => ({
      teacher_id: teacherId, course_id: courseId,
      subject_name: s.name, subject_type: s.type, pueblo_id: s.puebloId ?? null
    }))
  );
}

export async function getTeacherSubjects(teacherId: string): Promise<TeacherCourseSubject[]> {
  // Consultar asignaturas específicas
  const { data: subjects, error } = await supabase
    .from('teacher_course_subjects')
    .select('id, teacher_id, course_id, subject_name, subject_type, pueblo_id, courses(name, level_id, levels(name))')
    .eq('teacher_id', teacherId);
  throwIfError(error);

  if (subjects && subjects.length > 0) {
    return subjects.map((row: any) => ({
      id: row.id, teacherId,
      courseId: row.course_id, courseName: row.courses?.name || '',
      levelId: row.courses?.level_id || '', levelName: row.courses?.levels?.name || '',
      subjectName: row.subject_name,
      subjectType: (row.subject_type === 'group' ? 'group' : 'individual') as 'group' | 'individual',
      puebloId: row.pueblo_id || null
    }));
  }

  // Fallback: cursos asignados (modo no-CRA)
  const { data: tc } = await supabase
    .from('teacher_courses')
    .select('course_id, courses(name, level_id, levels(name))')
    .eq('teacher_id', teacherId);

  return (tc || []).map((row: any) => ({
    id: crypto.randomUUID(), teacherId,
    courseId: row.course_id, courseName: row.courses?.name || '',
    levelId: row.courses?.level_id || '', levelName: row.courses?.levels?.name || '',
    subjectName: 'General', subjectType: 'individual' as const
  }));
}

// ══════════════════════════════════════════════════════════
// BOLETINES
// ══════════════════════════════════════════════════════════

export async function getReportCard(courseId: string): Promise<ReportCardState | null> {
  const { data, error } = await supabase.from('report_cards').select('*').eq('course_id', courseId).maybeSingle();
  throwIfError(error);
  if (!data) return null;

  const sections = (data.sections || []).map((section: Section) => ({
    ...section,
    fields: section.fields.map((field: Field) => ({ ...field, type: field.type || 'select' }))
  }));

  return { id: data.id, courseId: data.course_id, sections, subjects: data.subjects || [] };
}

export async function getCourseData(courseId: string): Promise<any> {
  const { data, error } = await supabase.from('courses').select('*').eq('id', courseId).single();
  throwIfError(error);
  return data;
}

export const verifyTemplateAssignment = async (courseId: string): Promise<boolean> => {
  const { data } = await supabase.from('courses').select('template_id').eq('id', courseId).single();
  return !!data?.template_id;
};

export async function saveReportCard(reportCard: ReportCardState, _templateId: string): Promise<void> {
  const centerId = getCenterId();
  const sectionsWithFields = reportCard.sections.map((section: Section) => ({
    ...section,
    fields: section.fields.map((field: Field) => ({ ...field, type: field.type || 'select' }))
  }));

  const { error } = await supabase.from('report_cards').upsert({
    id: reportCard.id,
    course_id: reportCard.courseId,
    sections: sectionsWithFields,
    subjects: reportCard.subjects,
    center_id: centerId,
    updated_at: new Date().toISOString()
  }, { onConflict: 'course_id' });
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// GRUPOS DE CRITERIOS
// ══════════════════════════════════════════════════════════

export async function getCriteriaGroups(): Promise<CriteriaGroup[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('criteria_groups').select('*').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name, criteria: r.criteria }));
}

export async function saveCriteriaGroup(group: Omit<CriteriaGroup, 'id'>): Promise<CriteriaGroup> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('criteria_groups')
    .insert({ name: group.name, criteria: group.criteria, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name, criteria: data.criteria };
}

export async function deleteCriteriaGroup(id: string): Promise<void> {
  const { error } = await supabase.from('criteria_groups').delete().eq('id', id);
  throwIfError(error);
}

export async function updateCriteriaGroup(group: CriteriaGroup): Promise<void> {
  const { error } = await supabase.from('criteria_groups')
    .update({ name: group.name, criteria: group.criteria }).eq('id', group.id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// ASIGNAR PLANTILLA A CURSO
// ══════════════════════════════════════════════════════════

export async function assignTemplateToCourse(courseId: string, templateId: string): Promise<void> {
  const { error } = await supabase.from('courses').update({ template_id: templateId }).eq('id', courseId);
  throwIfError(error);
}

export async function executeWithRetry<T>(operation: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try { return await operation(); } catch (error) {
    if (retries > 0 && error instanceof Error && error.message.includes('ERR_INTERNET_DISCONNECTED')) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return executeWithRetry(operation, retries - 1, delay * 2);
    }
    throw error;
  }
}

export async function updateCourseTemplateAndYear(courseId: string, templateId: string, academicYear: string): Promise<void> {
  const { data: year, error: e1 } = await supabase.from('academic_years').select('id').eq('name', academicYear).single();
  if (e1 || !year) throw new Error(`No se encontró el año académico ${academicYear}`);

  const { error } = await supabase.from('courses')
    .update({ template_id: templateId, academic_year_id: year.id }).eq('id', courseId);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// NOTAS DE ALUMNOS
// ══════════════════════════════════════════════════════════

export async function getStudentGrades(studentId: string, courseId: string, evaluationPeriod: EvaluationPeriod = '1') {
  const { data, error } = await supabase.from('student_grades')
    .select('*').eq('student_id', studentId).eq('course_id', courseId).eq('evaluation_period', evaluationPeriod)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  throwIfError(error);
  if (!data) return null;

  return {
    id: data.id, studentId: data.student_id, courseId: data.course_id,
    templateId: data.template_id, academicYear: data.academic_year,
    grades: data.grades, additionalCriteria: data.additional_criteria || [],
    evaluationPeriod: data.evaluation_period as EvaluationPeriod
  };
}

export async function saveStudentGrades({
  studentId, courseId, templateId, academicYear, grades, additionalCriteria, evaluationPeriod = '1'
}: {
  studentId: string; courseId: string; templateId: string; academicYear: string;
  grades: Grades; additionalCriteria: AdditionalCriterion[]; evaluationPeriod?: EvaluationPeriod;
}): Promise<void> {
  const centerId = getCenterId();
  const { error } = await supabase.from('student_grades').upsert({
    student_id: studentId, course_id: courseId, template_id: templateId,
    academic_year: academicYear, grades, additional_criteria: additionalCriteria,
    evaluation_period: evaluationPeriod,
    center_id: centerId,
    updated_at: new Date().toISOString()
  }, { onConflict: 'student_id,course_id,template_id,academic_year,evaluation_period' });
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// CONFIGURACIÓN DEL CENTRO
// ══════════════════════════════════════════════════════════

export async function getCenterConfig() {
  const centerId = getCenterId();
  const { data } = await supabase.from('centers').select('*').eq('id', centerId).single();
  if (!data) return null;
  return {
    id: data.id, name: data.name, logo: data.logo || undefined,
    isCra: data.is_cra || false, reportCardTheme: data.report_card_theme || 'classic',
    language: data.language || 'es'
  };
}

export async function saveCenterConfig(config: { id: string; name: string; logo?: string; isCra?: boolean; reportCardTheme?: string; language?: string }) {
  const centerId = getCenterId();
  const { error } = await supabase.from('centers').update({
    name: config.name, logo: config.logo || null,
    is_cra: config.isCra || false, report_card_theme: config.reportCardTheme || 'classic',
    language: config.language || 'es', updated_at: new Date().toISOString()
  }).eq('id', centerId);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// PUEBLOS (CRA)
// ══════════════════════════════════════════════════════════

export async function getPueblos(): Promise<import('../../types').Pueblo[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('pueblos').select('id, name').eq('center_id', centerId).order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name }));
}

export async function createPueblo(name: string): Promise<import('../../types').Pueblo> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('pueblos').insert({ name, center_id: centerId }).select().single();
  throwIfError(error);
  return { id: data.id, name: data.name };
}

export async function updatePueblo(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('pueblos').update({ name }).eq('id', id);
  throwIfError(error);
}

export async function deletePueblo(id: string): Promise<void> {
  const { error } = await supabase.from('pueblos').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// ASIGNACIONES ASIGNATURA ↔ PUEBLO
// ══════════════════════════════════════════════════════════

export async function getSubjectPuebloAssignments(templateId: string): Promise<import('../../types').SubjectPuebloAssignment[]> {
  // Get subject IDs for the template
  const { data: subjects } = await supabase.from('course_template_subjects')
    .select('id').eq('template_id', templateId);
  const subjectIds = (subjects || []).map((s: any) => s.id);
  if (subjectIds.length === 0) return [];

  const { data, error } = await supabase.from('subject_pueblo_assignments')
    .select('template_subject_id, pueblo_id').in('template_subject_id', subjectIds);
  throwIfError(error);
  return (data || []).map(r => ({ subjectId: r.template_subject_id, puebloId: r.pueblo_id }));
}

export async function saveSubjectPuebloAssignments(
  templateId: string, assignments: import('../../types').SubjectPuebloAssignment[]
): Promise<void> {
  // Get subject IDs for this template to scope the delete
  const { data: subjects } = await supabase.from('course_template_subjects')
    .select('id').eq('template_id', templateId);
  const subjectIds = (subjects || []).map((s: any) => s.id);

  if (subjectIds.length > 0) {
    await supabase.from('subject_pueblo_assignments').delete().in('template_subject_id', subjectIds);
  }

  if (assignments.length > 0) {
    await supabase.from('subject_pueblo_assignments').insert(
      assignments.map(a => ({ template_subject_id: a.subjectId, pueblo_id: a.puebloId }))
    );
  }
}

// ══════════════════════════════════════════════════════════
// CRITERIOS DE EVALUACIÓN
// ══════════════════════════════════════════════════════════

export async function getEvaluationCriteria(): Promise<EvaluationCriterion[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('evaluation_criteria').select('*').eq('center_id', centerId).order('order');
  throwIfError(error);
  return (data || []).map(r => ({
    id: r.id, label: r.label, value: r.value, order: r.order, isDefault: r.is_default
  }));
}

export async function saveEvaluationCriterion(criterion: Omit<EvaluationCriterion, 'id'>): Promise<EvaluationCriterion> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('evaluation_criteria')
    .insert({ label: criterion.label, value: criterion.value, order: criterion.order, is_default: criterion.isDefault, center_id: centerId })
    .select().single();
  throwIfError(error);
  return { id: data.id, label: data.label, value: data.value, order: data.order, isDefault: data.is_default };
}

export async function updateEvaluationCriterion(id: string, criterion: Partial<EvaluationCriterion>): Promise<void> {
  const update: any = {};
  if (criterion.label !== undefined) update.label = criterion.label;
  if (criterion.value !== undefined) update.value = criterion.value;
  if (criterion.order !== undefined) update.order = criterion.order;
  if (criterion.isDefault !== undefined) update.is_default = criterion.isDefault;
  if (Object.keys(update).length > 0) {
    const { error } = await supabase.from('evaluation_criteria').update(update).eq('id', id);
    throwIfError(error);
  }
}

export async function deleteEvaluationCriterion(id: string): Promise<void> {
  const { error } = await supabase.from('evaluation_criteria').delete().eq('id', id);
  throwIfError(error);
}

export async function updateEvaluationCriteriaOrder(criteria: EvaluationCriterion[]): Promise<void> {
  const { error } = await supabase.from('evaluation_criteria').upsert(
    criteria.map((c, i) => ({ id: c.id, label: c.label, value: c.value, order: i, is_default: c.isDefault }))
  );
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// USUARIOS
// ══════════════════════════════════════════════════════════

export async function getUsers(): Promise<User[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('users').select('*').eq('center_id', centerId).order('username');
  throwIfError(error);
  return (data || []).map(r => ({
    id: r.id, username: r.username, role: r.role as 'admin' | 'director' | 'teacher',
    email: r.email, isActive: r.is_active,
    teacherId: r.teacher_id || undefined,
    centerId: r.center_id,
    permissions: r.permissions || []
  }));
}

// El alta, la edición, el borrado y el cambio de contraseña de usuarios pasan
// por api/admin-users porque necesitan la service role key: crear cuentas en
// auth.users y escribir en public.users, que tras la migración 004 es de solo
// lectura desde el cliente. La contraseña ya no viaja ni se guarda aquí: la
// gestiona Supabase Auth con hash bcrypt en el servidor.

export async function createUser(userData: Partial<User>): Promise<void> {
  if (!userData.username || !userData.role || !userData.email) throw new Error('Faltan campos requeridos');
  if (!userData.password) throw new Error('Se requiere una contraseña inicial');

  await adminUsers('create', {
    username: userData.username,
    email: userData.email,
    password: userData.password,
    role: userData.role,
    teacherId: userData.teacherId,
    isActive: userData.isActive ?? true,
    permissions: userData.permissions,
    centerId: (isGlobalAdmin() && userData.centerId) ? userData.centerId : getCenterId(),
  });
}

export async function updateUser(id: string, userData: Partial<User>): Promise<void> {
  if (!userData.username || !userData.role || !userData.email) throw new Error('Faltan campos requeridos');

  await adminUsers('update', {
    id,
    username: userData.username,
    email: userData.email,
    role: userData.role,
    teacherId: userData.teacherId,
    isActive: userData.isActive ?? true,
    permissions: userData.permissions,
    centerId: isGlobalAdmin() ? userData.centerId : undefined,
  });

  // Cambiar la contraseña desde el formulario de edición se hace aparte:
  // es una operación distinta contra Supabase Auth.
  if (userData.password) await updateUserPassword(id, userData.password);
}

export async function deleteUser(id: string): Promise<void> {
  await adminUsers('delete', { id });
}

export async function updateUserPassword(id: string, newPassword: string): Promise<void> {
  await adminUsers('setPassword', { id, password: newPassword });
}

// Las funciones de sembrado heredadas (updateAdminUser, migrateTeachersToUsers,
// syncTeachersAndUsers, initializeDatabase) se han eliminado en la migración a
// Supabase Auth. Creaban usuarios con contraseñas fijas en claro ('admin',
// 'director', 'password') y ya no se ejecutaban: initializeDatabase fallaba
// siempre en getCenterId() al arrancar, antes de que hubiera sesión, y el error
// se descartaba en silencio. El alta de usuarios pasa ahora por api/admin-users.

// ══════════════════════════════════════════════════════════
// IMPORTAR ALUMNOS
// ══════════════════════════════════════════════════════════

export async function importStudents(students: any[]): Promise<Student[]> {
  const centerId = getCenterId();
  const imported: Student[] = [];
  for (const s of students) {
    const { data, error } = await supabase.from('students')
      .insert({
        first_name: s.firstName, last_name: s.lastName, identifier: s.identifier,
        email: s.email || null, phone: s.phone || null, address: s.address || null,
        birth_date: s.birthDate || null, course_id: s.courseId, instrument_id: s.instrumentId,
        pueblo_id: s.puebloId || null, center_id: centerId
      })
      .select(STUDENT_SELECT).single();
    if (error) { console.error('Error importing student:', error); continue; }
    imported.push(mapStudent(data));
  }
  return imported;
}

// ══════════════════════════════════════════════════════════
// EMAIL CONFIGS
// ══════════════════════════════════════════════════════════

export async function getEmailConfigs(): Promise<EmailConfig[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('email_configs').select('*').eq('center_id', centerId).order('is_default', { ascending: false });
  throwIfError(error);
  return (data || []).map(r => ({
    id: r.id, email: r.email,
    password: r.password ? decryptPassword(r.password) : undefined,
    isDefault: r.is_default, authType: r.auth_type as 'password' | 'oauth',
    oauthCredentials: r.oauth_credentials ? JSON.parse(decryptPassword(r.oauth_credentials)) : undefined,
    templateId: r.template_id || undefined
  }));
}

export async function saveEmailConfig(config: {
  email: string; password?: string; isDefault: boolean;
  authType: 'password' | 'oauth'; oauthCredentials?: OAuthCredentials;
}) {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('email_configs')
    .insert({
      email: config.email,
      password: config.password ? encryptPassword(config.password) : null,
      is_default: config.isDefault, auth_type: config.authType,
      oauth_credentials: config.oauthCredentials ? encryptPassword(JSON.stringify(config.oauthCredentials)) : null,
      center_id: centerId
    })
    .select().single();
  throwIfError(error);
  return { id: data.id, email: data.email, password: config.password, isDefault: data.is_default, authType: config.authType, oauthCredentials: config.oauthCredentials };
}

export async function updateEmailConfig(id: string, config: {
  email?: string; password?: string; isDefault?: boolean;
  oauthCredentials?: OAuthCredentials; templateId?: string;
}): Promise<void> {
  const update: any = {};
  if (config.email !== undefined) update.email = config.email;
  if (config.password !== undefined) update.password = encryptPassword(config.password);
  if (config.isDefault !== undefined) update.is_default = config.isDefault;
  if (config.oauthCredentials !== undefined) update.oauth_credentials = encryptPassword(JSON.stringify(config.oauthCredentials));
  if (config.templateId !== undefined) update.template_id = config.templateId || null;

  if (config.isDefault) {
    await supabase.from('email_configs').update({ is_default: false }).neq('id', id);
  }
  if (Object.keys(update).length > 0) {
    const { error } = await supabase.from('email_configs').update(update).eq('id', id);
    throwIfError(error);
  }
}

export async function deleteEmailConfig(id: string) {
  const { error } = await supabase.from('email_configs').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// EMAIL TEMPLATES
// ══════════════════════════════════════════════════════════

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('email_templates').select('*').eq('center_id', centerId).order('is_default', { ascending: false });
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name, subject: r.subject, body: r.body, isDefault: r.is_default }));
}

export async function saveEmailTemplate(template: Omit<EmailTemplate, 'id'>): Promise<EmailTemplate> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('email_templates')
    .insert({ name: template.name, subject: template.subject, body: template.body, is_default: template.isDefault, center_id: centerId })
    .select().single();
  throwIfError(error);
  return { id: data.id, name: data.name, subject: data.subject, body: data.body, isDefault: data.is_default };
}

export async function updateEmailTemplate(id: string, template: { name?: string; subject?: string; body?: string; isDefault?: boolean }) {
  const update: any = {};
  if (template.name !== undefined) update.name = template.name;
  if (template.subject !== undefined) update.subject = template.subject;
  if (template.body !== undefined) update.body = template.body;
  if (template.isDefault !== undefined) update.is_default = template.isDefault;

  if (Object.keys(update).length > 0) {
    const { error } = await supabase.from('email_templates').update(update).eq('id', id);
    throwIfError(error);
  }

  if (template.isDefault) {
    await supabase.from('email_templates').update({ is_default: false }).neq('id', id);
  }
}

export async function deleteEmailTemplate(id: string) {
  await supabase.from('email_configs').update({ template_id: null }).eq('template_id', id);
  const { error } = await supabase.from('email_templates').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// OAUTH CONFIG
// ══════════════════════════════════════════════════════════

export async function getOAuthConfig() {
  const centerId = getCenterId();
  const { data } = await supabase.from('oauth_config').select('*').eq('center_id', centerId).eq('is_active', true).limit(1).maybeSingle();
  if (!data) return null;
  return {
    id: data.id, clientId: data.client_id,
    clientSecret: decryptPassword(data.client_secret),
    redirectUri: data.redirect_uri, isActive: data.is_active
  };
}

export async function saveOAuthConfig(config: { clientId: string; clientSecret: string; redirectUri: string }) {
  const centerId = getCenterId();
  await supabase.from('oauth_config').update({ is_active: false }).eq('center_id', centerId).eq('is_active', true);
  const { data, error } = await supabase.from('oauth_config')
    .insert({ client_id: config.clientId, client_secret: encryptPassword(config.clientSecret), redirect_uri: config.redirectUri, is_active: true, center_id: centerId })
    .select().single();
  throwIfError(error);
  return { id: data.id, ...config, isActive: true };
}

export async function updateOAuthConfig(id: string, config: {
  clientId?: string; clientSecret?: string; redirectUri?: string; isActive?: boolean;
}) {
  const centerId = getCenterId();
  const update: any = {};
  if (config.clientId !== undefined) update.client_id = config.clientId;
  if (config.clientSecret !== undefined) update.client_secret = encryptPassword(config.clientSecret);
  if (config.redirectUri !== undefined) update.redirect_uri = config.redirectUri;
  if (config.isActive !== undefined) {
    if (config.isActive) await supabase.from('oauth_config').update({ is_active: false }).eq('center_id', centerId).eq('is_active', true);
    update.is_active = config.isActive;
  }
  if (Object.keys(update).length > 0) {
    const { error } = await supabase.from('oauth_config').update(update).eq('id', id);
    throwIfError(error);
  }
}

// ══════════════════════════════════════════════════════════
// GESTIÓN DE CENTROS (admin global)
// ══════════════════════════════════════════════════════════

export async function getCenters(): Promise<Center[]> {
  const { data, error } = await supabase.from('centers').select('*').order('name');
  throwIfError(error);
  return (data || []).map(r => ({ id: r.id, name: r.name, logo: r.logo || undefined, isCra: r.is_cra || false }));
}

export async function createCenter(center: Omit<Center, 'id'>): Promise<Center> {
  const { data, error } = await supabase.from('centers')
    .insert({ name: center.name, logo: center.logo || null, is_cra: center.isCra || false })
    .select().single();
  throwIfError(error);
  return { id: data.id, name: data.name, logo: data.logo || undefined, isCra: data.is_cra || false };
}

export async function updateCenter(id: string, center: Partial<Center>): Promise<void> {
  const update: any = {};
  if (center.name !== undefined) update.name = center.name;
  if (center.logo !== undefined) update.logo = center.logo || null;
  if (center.isCra !== undefined) update.is_cra = center.isCra;
  update.updated_at = new Date().toISOString();
  const { error } = await supabase.from('centers').update(update).eq('id', id);
  throwIfError(error);
}

export async function deleteCenter(id: string): Promise<void> {
  const { error } = await supabase.from('centers').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// PERIODOS DE EVALUACIÓN
// ══════════════════════════════════════════════════════════

export async function getEvaluationPeriods(): Promise<EvaluationPeriodConfig[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('evaluation_periods')
    .select('*').eq('center_id', centerId).order('sort_order');
  throwIfError(error);
  return (data || []).map(r => ({
    id: r.id, key: r.key, label: r.label, sortOrder: r.sort_order
  }));
}

export async function createEvaluationPeriod(key: string, label: string, sortOrder: number): Promise<EvaluationPeriodConfig> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('evaluation_periods')
    .insert({ center_id: centerId, key, label, sort_order: sortOrder })
    .select().single();
  throwIfError(error);
  return { id: data.id, key: data.key, label: data.label, sortOrder: data.sort_order };
}

export async function updateEvaluationPeriod(id: string, key: string, label: string, sortOrder: number): Promise<void> {
  const { error } = await supabase.from('evaluation_periods')
    .update({ key, label, sort_order: sortOrder }).eq('id', id);
  throwIfError(error);
}

export async function deleteEvaluationPeriod(id: string): Promise<void> {
  const { error } = await supabase.from('evaluation_periods').delete().eq('id', id);
  throwIfError(error);
}

// ══════════════════════════════════════════════════════════
// DASHBOARD STATS
// ══════════════════════════════════════════════════════════

export interface DashboardOverview {
  activeYear: string | null;
  activeYearId: string | null;
  /** Año más reciente que existe pero no está marcado como activo. */
  newerYear: string | null;
  periodsCount: number;
  coursesInActiveYear: number;
  teachers: number;
  teachersWithoutSubjects: number;
  teacherAssignments: number;
  students: number;
  studentsInActiveYear: number;
  studentsWithoutCourse: number;
  emailsSent: number;
  emailsFailed: number;
}

/**
 * Resumen del centro para el panel de inicio: lo justo para saber qué falta
 * por configurar y qué incoherencias hay, sin cargar listados completos.
 */
export interface TeacherClass {
  courseId: string;
  courseName: string;
  academicYearId: string | null;
  subjects: string[];
}

/**
 * Cursos y asignaturas que imparte un profesor. Las asignaciones se guardan
 * una vez por pueblo del CRA, así que la misma asignatura puede venir repetida
 * varias veces para un curso: se deduplica.
 */
export async function getTeacherClasses(teacherId: string): Promise<TeacherClass[]> {
  const { data: filas, error } = await supabase.from('teacher_course_subjects')
    .select('course_id, subject_name').eq('teacher_id', teacherId);
  throwIfError(error);
  if (!filas?.length) return [];

  const ids = [...new Set(filas.map(f => f.course_id))];
  const { data: cursos, error: cErr } = await supabase.from('courses')
    .select('id, name, academic_year_id').in('id', ids);
  throwIfError(cErr);

  return (cursos || [])
    .map(c => ({
      courseId: c.id,
      courseName: c.name,
      academicYearId: c.academic_year_id ?? null,
      subjects: [...new Set(filas.filter(f => f.course_id === c.id).map(f => f.subject_name))].sort(),
    }))
    .sort((a, b) => a.courseName.localeCompare(b.courseName, 'es', { numeric: true }));
}

export async function getDashboardOverview(): Promise<DashboardOverview> {
  const centerId = getCenterId();
  const cuenta = { count: 'exact' as const, head: true };

  const [years, periods, courses, teachers, students, emails] = await Promise.all([
    supabase.from('academic_years').select('id, name, is_active').eq('center_id', centerId),
    supabase.from('evaluation_periods').select('id', cuenta).eq('center_id', centerId),
    supabase.from('courses').select('id, academic_year_id').eq('center_id', centerId),
    supabase.from('teachers').select('id').eq('center_id', centerId),
    supabase.from('students').select('id, course_id').eq('center_id', centerId),
    supabase.from('email_history').select('status').eq('center_id', centerId),
  ]);
  for (const r of [years, periods, courses, teachers, students]) throwIfError(r.error);

  const listaAnos = years.data || [];
  const activo = listaAnos.find(y => y.is_active) || null;
  // Los nombres tienen forma "2024-25": el orden alfabético coincide con el cronológico.
  const masReciente = [...listaAnos].sort((a, b) => b.name.localeCompare(a.name))[0] || null;

  const cursosActivos = new Set(
    (courses.data || []).filter(c => !c.academic_year_id || (activo && c.academic_year_id === activo.id)).map(c => c.id)
  );
  const alumnos = students.data || [];
  const profesores = teachers.data || [];

  // Un profesor está "sin asignaturas" si no aparece ni en las asignaturas
  // por curso ni en las grupales.
  let conAsignatura = new Set<string>();
  let asignaciones = 0;
  if (profesores.length) {
    const ids = profesores.map(t => t.id);
    const [tcs, tgs] = await Promise.all([
      supabase.from('teacher_course_subjects').select('teacher_id').in('teacher_id', ids),
      supabase.from('teacher_group_subjects').select('teacher_id').in('teacher_id', ids),
    ]);
    const filas = [...(tcs.data || []), ...(tgs.data || [])];
    asignaciones = filas.length;
    conAsignatura = new Set(filas.map(f => f.teacher_id));
  }

  const envios = emails.data || [];

  return {
    activeYear: activo?.name ?? null,
    activeYearId: activo?.id ?? null,
    newerYear: masReciente && activo && masReciente.id !== activo.id ? masReciente.name : null,
    periodsCount: periods.count ?? 0,
    coursesInActiveYear: cursosActivos.size,
    teachers: profesores.length,
    teachersWithoutSubjects: profesores.filter(t => !conAsignatura.has(t.id)).length,
    teacherAssignments: asignaciones,
    students: alumnos.length,
    studentsInActiveYear: alumnos.filter(a => a.course_id && cursosActivos.has(a.course_id)).length,
    studentsWithoutCourse: alumnos.filter(a => !a.course_id).length,
    emailsSent: envios.filter(e => e.status === 'sent' || e.status === 'opened').length,
    emailsFailed: envios.filter(e => e.status === 'failed').length,
  };
}

export async function getDashboardStats(academicYear: string, evaluationPeriod: string, onlyCourseIds?: string[]): Promise<{
  totalStudents: number;
  studentsWithGrades: number;
  courseStats: { courseId: string; courseName: string; total: number; withGrades: number }[];
}> {
  const centerId = getCenterId();

  // Look up academic year ID from name
  const { data: yearRow } = await supabase.from('academic_years')
    .select('id').eq('name', academicYear).eq('center_id', centerId).single();
  if (!yearRow) return { totalStudents: 0, studentsWithGrades: 0, courseStats: [] };

  // Get courses for this center and academic year
  const { data: courses, error: cErr } = await supabase.from('courses')
    .select('id, name')
    .eq('center_id', centerId)
    // Cursos permanentes (sin año) o ligados a este año concreto.
    .or(`academic_year_id.is.null,academic_year_id.eq.${yearRow.id}`);
  throwIfError(cErr);

  if (!courses?.length) return { totalStudents: 0, studentsWithGrades: 0, courseStats: [] };
  if (onlyCourseIds) {
    const permitidos = new Set(onlyCourseIds);
    courses.splice(0, courses.length, ...courses.filter(c => permitidos.has(c.id)));
    if (!courses.length) return { totalStudents: 0, studentsWithGrades: 0, courseStats: [] };
  }

  const courseIds = courses.map(c => c.id);
  const courseNameMap = new Map(courses.map(c => [c.id, c.name]));

  // Get all students for those courses
  const { data: students, error: sErr } = await supabase.from('students')
    .select('id, course_id')
    .in('course_id', courseIds);
  throwIfError(sErr);

  // Get all student_grades for this period
  const { data: grades, error: gErr } = await supabase.from('student_grades')
    .select('student_id, course_id')
    .eq('evaluation_period', evaluationPeriod)
    .eq('academic_year', academicYear);
  throwIfError(gErr);

  const gradeSet = new Set((grades || []).map(g => `${g.student_id}:${g.course_id}`));

  const courseMap = new Map<string, { courseName: string; total: number; withGrades: number }>();
  let totalStudents = 0;
  let studentsWithGrades = 0;

  for (const s of students || []) {
    totalStudents++;
    const courseName = courseNameMap.get(s.course_id) || '';
    const hasGrade = gradeSet.has(`${s.id}:${s.course_id}`);
    if (hasGrade) studentsWithGrades++;

    const existing = courseMap.get(s.course_id);
    if (existing) {
      existing.total++;
      if (hasGrade) existing.withGrades++;
    } else {
      courseMap.set(s.course_id, { courseName, total: 1, withGrades: hasGrade ? 1 : 0 });
    }
  }

  return {
    totalStudents,
    studentsWithGrades,
    courseStats: Array.from(courseMap.entries()).map(([courseId, stat]) => ({
      courseId,
      ...stat
    })),
  };
}

// ══════════════════════════════════════════════════════════
// EMAIL HISTORY
// ══════════════════════════════════════════════════════════

export async function saveEmailHistory(record: {
  studentId?: string; courseId?: string; evaluationPeriod?: string;
  academicYear?: string; recipientEmail: string; subject: string;
  status: 'sent' | 'failed'; errorMessage?: string;
}): Promise<{ trackingId: string }> {
  const centerId = getCenterId();
  const userId = useAuth.getState().user?.id;
  const { data, error } = await supabase.from('email_history').insert({
    student_id: record.studentId || null,
    course_id: record.courseId || null,
    evaluation_period: record.evaluationPeriod || null,
    academic_year: record.academicYear || null,
    recipient_email: record.recipientEmail,
    subject: record.subject,
    status: record.status,
    error_message: record.errorMessage || null,
    sent_by: userId || null,
    center_id: centerId
  }).select('tracking_id').single();
  throwIfError(error);
  return { trackingId: data!.tracking_id };
}

export async function getEmailHistory(): Promise<EmailHistoryRecord[]> {
  const centerId = getCenterId();
  const { data, error } = await supabase.from('email_history')
    .select(`
      *,
      students(first_name, last_name),
      courses(name)
    `)
    .eq('center_id', centerId)
    .order('created_at', { ascending: false })
    .limit(200);
  throwIfError(error);
  return (data || []).map(r => ({
    id: r.id,
    studentId: r.student_id,
    courseId: r.course_id,
    evaluationPeriod: r.evaluation_period,
    academicYear: r.academic_year,
    recipientEmail: r.recipient_email,
    subject: r.subject,
    status: r.status,
    openedAt: r.opened_at,
    trackingId: r.tracking_id,
    errorMessage: r.error_message,
    sentBy: r.sent_by,
    createdAt: r.created_at,
    studentName: r.students ? `${r.students.first_name} ${r.students.last_name}` : undefined,
    courseName: r.courses?.name || undefined,
  }));
}

export async function markEmailOpened(trackingId: string): Promise<void> {
  await supabase.from('email_history')
    .update({ status: 'opened', opened_at: new Date().toISOString() })
    .eq('tracking_id', trackingId)
    .eq('status', 'sent');
}

export async function deleteEmailHistory(ids: string[]): Promise<void> {
  const { error } = await supabase.from('email_history').delete().in('id', ids);
  throwIfError(error);
}

export async function importCenterConfig(sourceCenterId: string, targetCenterId: string, options: {
  levels?: boolean; instruments?: boolean; groupSubjects?: boolean;
  evaluationCriteria?: boolean; courseTemplates?: boolean;
}): Promise<void> {
  if (options.levels) {
    const { data } = await supabase.from('levels').select('name, description').eq('center_id', sourceCenterId);
    if (data?.length) {
      const existing = await supabase.from('levels').select('name').eq('center_id', targetCenterId);
      const existingNames = new Set((existing.data || []).map(r => r.name));
      const toInsert = data.filter(r => !existingNames.has(r.name)).map(r => ({ ...r, center_id: targetCenterId }));
      if (toInsert.length) await supabase.from('levels').insert(toInsert);
    }
  }
  if (options.instruments) {
    const { data } = await supabase.from('instruments').select('name').eq('center_id', sourceCenterId);
    if (data?.length) {
      const existing = await supabase.from('instruments').select('name').eq('center_id', targetCenterId);
      const existingNames = new Set((existing.data || []).map(r => r.name));
      const toInsert = data.filter(r => !existingNames.has(r.name)).map(r => ({ ...r, center_id: targetCenterId }));
      if (toInsert.length) await supabase.from('instruments').insert(toInsert);
    }
  }
  if (options.groupSubjects) {
    const { data } = await supabase.from('group_subjects').select('name').eq('center_id', sourceCenterId);
    if (data?.length) {
      const existing = await supabase.from('group_subjects').select('name').eq('center_id', targetCenterId);
      const existingNames = new Set((existing.data || []).map(r => r.name));
      const toInsert = data.filter(r => !existingNames.has(r.name)).map(r => ({ ...r, center_id: targetCenterId }));
      if (toInsert.length) await supabase.from('group_subjects').insert(toInsert);
    }
  }
  if (options.evaluationCriteria) {
    const { data } = await supabase.from('evaluation_criteria').select('label, value, order, is_default').eq('center_id', sourceCenterId);
    if (data?.length) {
      const existing = await supabase.from('evaluation_criteria').select('label').eq('center_id', targetCenterId);
      const existingLabels = new Set((existing.data || []).map(r => r.label));
      const toInsert = data.filter(r => !existingLabels.has(r.label)).map(r => ({ ...r, center_id: targetCenterId }));
      if (toInsert.length) await supabase.from('evaluation_criteria').insert(toInsert);
    }
  }
}

// ── Signatures ─────────────────────────────────────────────

export async function getSignature(userId: string): Promise<string | null> {
  const centerId = getCenterId();
  const { data } = await supabase.from('signatures').select('image_data').eq('user_id', userId).eq('center_id', centerId).maybeSingle();
  return data?.image_data || null;
}

export async function saveSignature(userId: string, imageData: string): Promise<void> {
  const centerId = getCenterId();
  const { error } = await supabase.from('signatures').upsert({
    user_id: userId, center_id: centerId, image_data: imageData, updated_at: new Date().toISOString()
  }, { onConflict: 'user_id,center_id' });
  throwIfError(error);
}

export async function deleteSignature(userId: string): Promise<void> {
  const centerId = getCenterId();
  const { error } = await supabase.from('signatures').delete().eq('user_id', userId).eq('center_id', centerId);
  throwIfError(error);
}

export async function getSignaturesForCenter(): Promise<{ userId: string; imageData: string }[]> {
  const centerId = getCenterId();
  const { data } = await supabase
    .from('signatures')
    .select('user_id, image_data')
    .eq('center_id', centerId);
  return (data || []).map(r => ({ userId: r.user_id, imageData: r.image_data }));
}

// ── Notifications ──────────────────────────────────────────

export interface NotificationRecord {
  id: string;
  centerId: string;
  userId: string | null;
  type: string;
  title: string;
  message: string | null;
  isRead: boolean;
  metadata: Record<string, any>;
  createdAt: string;
}

export async function getNotifications(userId: string): Promise<NotificationRecord[]> {
  const centerId = getCenterId();
  const { data } = await supabase
    .from('notifications')
    .select('*')
    .eq('center_id', centerId)
    .or(`user_id.eq.${userId},user_id.is.null`)
    .order('created_at', { ascending: false })
    .limit(50);
  return (data || []).map(r => ({
    id: r.id, centerId: r.center_id, userId: r.user_id, type: r.type,
    title: r.title, message: r.message, isRead: r.is_read,
    metadata: r.metadata || {}, createdAt: r.created_at
  }));
}

export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id);
  throwIfError(error);
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const centerId = getCenterId();
  const { error } = await supabase.from('notifications').update({ is_read: true })
    .eq('center_id', centerId)
    .or(`user_id.eq.${userId},user_id.is.null`)
    .eq('is_read', false);
  throwIfError(error);
}

export async function createNotification(n: { userId?: string; type: string; title: string; message?: string; metadata?: Record<string, any> }): Promise<void> {
  const centerId = getCenterId();
  const { error } = await supabase.from('notifications').insert({
    center_id: centerId, user_id: n.userId || null, type: n.type,
    title: n.title, message: n.message || null, metadata: n.metadata || {}
  });
  throwIfError(error);
}

// ── Duplicate Course Config ────────────────────────────────

export async function duplicateCourseConfig(sourceAcademicYear: string, targetAcademicYear: string): Promise<number> {
  const centerId = getCenterId();
  // Get courses from source year
  const { data: sourceCourses } = await supabase.from('courses')
    .select('name, level_id, template_id')
    .eq('center_id', centerId)
    .eq('academic_year', sourceAcademicYear);
  if (!sourceCourses?.length) return 0;

  // Get existing courses in target year
  const { data: existingCourses } = await supabase.from('courses')
    .select('name')
    .eq('center_id', centerId)
    .eq('academic_year', targetAcademicYear);
  const existingNames = new Set((existingCourses || []).map(c => c.name));

  const toInsert = sourceCourses
    .filter(c => !existingNames.has(c.name))
    .map(c => ({
      name: c.name, level_id: c.level_id, template_id: c.template_id,
      center_id: centerId, academic_year: targetAcademicYear
    }));
  if (toInsert.length) {
    const { error } = await supabase.from('courses').insert(toInsert);
    throwIfError(error);
  }
  return toInsert.length;
}

// ── Advanced Statistics ────────────────────────────────────

export interface CourseStats {
  courseId: string;
  courseName: string;
  studentCount: number;
  averageGlobal: number;
  distribution: { label: string; count: number }[];
}

export async function getAdvancedStats(academicYear: string, evaluationPeriod: string): Promise<CourseStats[]> {
  const centerId = getCenterId();
  // Look up academic year ID
  const { data: yearRow } = await supabase.from('academic_years')
    .select('id').eq('name', academicYear).eq('center_id', centerId).single();
  if (!yearRow) return [];
  // Get courses
  const { data: courses } = await supabase.from('courses')
    .select('id, name').eq('center_id', centerId).eq('academic_year_id', yearRow.id);
  if (!courses?.length) return [];

  const results: CourseStats[] = [];
  for (const course of courses) {
    const { data: students } = await supabase.from('students')
      .select('id').eq('course_id', course.id).eq('center_id', centerId);
    if (!students?.length) {
      results.push({ courseId: course.id, courseName: course.name, studentCount: 0, averageGlobal: 0, distribution: [] });
      continue;
    }
    const studentIds = students.map(s => s.id);
    const { data: grades } = await supabase.from('student_grades')
      .select('grades').in('student_id', studentIds).eq('evaluation_period', evaluationPeriod);
    
    const globals: number[] = [];
    (grades || []).forEach(g => {
      const gr = g.grades as Record<string, any>;
      Object.values(gr).forEach((section: any) => {
        if (section?.global) {
          const num = parseFloat(section.global);
          if (!isNaN(num)) globals.push(num);
        }
      });
    });

    const avg = globals.length ? globals.reduce((a, b) => a + b, 0) / globals.length : 0;
    const dist = [
      { label: 'Sobresaliente (9-10)', count: globals.filter(g => g >= 9).length },
      { label: 'Notable (7-8.9)', count: globals.filter(g => g >= 7 && g < 9).length },
      { label: 'Bien (6-6.9)', count: globals.filter(g => g >= 6 && g < 7).length },
      { label: 'Suficiente (5-5.9)', count: globals.filter(g => g >= 5 && g < 6).length },
      { label: 'Insuficiente (<5)', count: globals.filter(g => g < 5).length },
    ];
    results.push({ courseId: course.id, courseName: course.name, studentCount: students.length, averageGlobal: Math.round(avg * 100) / 100, distribution: dist });
  }
  return results;
}

// ── Grade History Timeline ─────────────────────────────────

export interface GradeHistoryEntry {
  evaluationPeriod: string;
  grades: Record<string, { global: string; attendance: string; [key: string]: any }>;
}

export async function getStudentGradeHistory(studentId: string, courseId: string): Promise<GradeHistoryEntry[]> {
  const centerId = getCenterId();
  const { data } = await supabase.from('student_grades')
    .select('evaluation_period, grades, updated_at')
    .eq('student_id', studentId)
    .eq('course_id', courseId)
    .eq('center_id', centerId)
    .order('evaluation_period', { ascending: true })
    .order('updated_at', { ascending: false });
  // Deduplicate: keep latest entry per evaluation_period
  const seen = new Set<string>();
  return (data || []).filter(r => {
    if (seen.has(r.evaluation_period)) return false;
    seen.add(r.evaluation_period);
    return true;
  }).map(r => ({
    evaluationPeriod: r.evaluation_period,
    grades: r.grades as any,
  }));
}

// Re-export schema (no-op con Supabase)
export { createSchema } from './schema';
